// An audition on the stage, one voice at a time (spec-v4 section 8.3): the candidate in focus large, the rest as
// chips marked heard / now / next. Nothing autoplays: the Director plays each with play_and_wait (the shared player),
// or the user taps play. "Pick" sets the voice the way the voice cards do.
import { useEffect, useState } from "react";
import { useVoice, voiceNameOf } from "../actions";
import { toggleAudio } from "../audio";
import { Icon } from "../components/bits";
import { director, tellDirector } from "../director";
import { onStage, pauseStage, stageVideo } from "../media";
import { isActive } from "../playback";
import { markActivity, useStore } from "../store";
import type { Artifact, StageItem } from "../types";
import { dispatchStage } from "./actions";

type Audition = Extract<StageItem, { kind: "audition" }>;

const idOf = (a: Artifact) => a.voiceId ?? a.url ?? a.title;
const keyOf = (a: Artifact) => `stage:${idOf(a)}`;
const clock = (s: number) => `${Math.floor(s / 60)}:${String(Math.floor(s % 60)).padStart(2, "0")}`;

/** What the Director hears when the user taps Pick on an audition. */
export const auditionPickLine = (a: Artifact) => `[app] The user picked ${voiceNameOf(a)} by tapping (voice ${a.voiceId ?? "?"}).`;

/** Fixed bars seeded by the name, so each voice looks a little different (the audio cards' glyph, bigger). */
function Wave({ seed, on }: { seed: string; on: boolean }) {
  let h = 7;
  for (const c of seed) h = (h * 31 + c.charCodeAt(0)) >>> 0;
  const bars = Array.from({ length: 32 }, (_, i) => {
    h = (h * 1664525 + 1013904223) >>> 0;
    const env = Math.sin((i / 31) * Math.PI) * 0.7 + 0.3;
    return 4 + Math.round((((h >>> 8) % 100) / 100) * 26 * env);
  });
  return (
    <svg className={`stg-wave${on ? " is-on" : ""}`} viewBox="0 0 128 32" preserveAspectRatio="none" aria-hidden>
      {bars.map((b, i) => (
        <rect key={i} x={i * 4} y={16 - b / 2} width="2.2" height={b} rx="1.1" />
      ))}
    </svg>
  );
}

/** The shared player's position while it plays this url (the Director's play_and_wait goes through it). */
function useClock(url: string | undefined, on: boolean) {
  const [t, setT] = useState<{ at: number; of: number } | null>(null);
  useEffect(() => {
    if (!on || !url) return setT(null);
    const read = () => {
      const v = stageVideo();
      setT({ at: v.currentTime || 0, of: Number.isFinite(v.duration) ? v.duration : 0 });
    };
    read();
    return onStage("timeupdate", read);
  }, [url, on]);
  return t;
}

export function AuditionView({ item, focus }: { item: Audition; focus: number }) {
  const heardHere = useStore((s) => s.stage.heard);
  const tapped = useStore((s) => s.playing);
  const pb = useStore((s) => s.playback);
  const heard = new Set([...(item.heard ?? []), ...heardHere]);
  const now = item.candidates[focus] ?? item.candidates[0]!;
  const viaDirector = isActive(pb) && pb.req?.what === "audio" && !!now.url && pb.req.url === now.url;
  const playing = tapped === keyOf(now) || (viaDirector && pb.phase === "playing");
  const t = useClock(now.url, viaDirector);

  // the Director played a candidate (play_and_wait): put it in focus and mark it heard
  useEffect(() => {
    if (!isActive(pb) || pb.req?.what !== "audio") return;
    const i = item.candidates.findIndex((c) => c.url && c.url === pb.req!.url);
    if (i < 0) return;
    dispatchStage({ type: "focus", index: i });
    dispatchStage({ type: "heard", id: idOf(item.candidates[i]!) });
  }, [pb.req?.url, pb.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  const play = (a: Artifact, i: number) => {
    markActivity();
    dispatchStage({ type: "focus", index: i });
    if (viaDirector && a === now) return pauseStage();
    dispatchStage({ type: "heard", id: idOf(a) });
    toggleAudio(keyOf(a), a.url);
    if (tapped !== keyOf(a)) director.context(`[app] The user tapped play on ${voiceNameOf(a)} (voice ${a.voiceId ?? "?"}).`);
  };
  // V4-K1: a pick is something the Director answers ("Eric it is"): an [app] user message once it isn't speaking
  const pick = async (a: Artifact) => {
    markActivity();
    await useVoice(a);
    tellDirector(auditionPickLine(a));
  };
  const name = voiceNameOf(now);
  const desc = now.note ?? (now.title !== name ? now.title.slice(name.length).replace(/^[\s,·:–-]+/, "") : "");

  return (
    <div className="stg-aud">
      <div className={`stg-aud-card${playing ? " is-on" : ""}`}>
        <button data-action="stage.play" type="button" className="stg-aud-play" onClick={() => play(now, focus)} disabled={!now.url} aria-label={playing ? `Pause ${name}` : `Play ${name}`}>
          <Icon name={playing ? "pause" : "play"} size={26} />
        </button>
        <Wave seed={name + (now.voiceId ?? "")} on={playing} />
        <p className="stg-aud-name">{name}</p>
        <p className="stg-aud-desc">
          {desc && <span>{desc}</span>}
          {t && t.of > 0 && (
            <span className="cao-timecode">
              {clock(t.at)}/{clock(t.of)}
            </span>
          )}
        </p>
        {now.text && <p className="stg-quote">“{now.text}”</p>}
      </div>
      <ul className="stg-chips" aria-label="Voices">
        {item.candidates.map((a, i) => {
          const state = i === focus ? "now" : heard.has(idOf(a)) ? "heard" : "next";
          return (
            <li key={idOf(a)}>
              <button data-action="stage.focus" type="button" className={`stg-chip is-${state}`} onClick={() => dispatchStage({ type: "focus", index: i })} aria-current={i === focus ? "true" : undefined}>
                <span className="stg-chip-mark" aria-hidden>
                  {state === "heard" ? <Icon name="check" size={14} /> : null}
                </span>
                {voiceNameOf(a)}
                <span className="sr-only">{state === "heard" ? " (heard)" : state === "now" ? " (now)" : " (next)"}</span>
              </button>
            </li>
          );
        })}
      </ul>
      <div className="stg-actions">
        <button data-action="stage.play" type="button" className="btn btn-quiet" onClick={() => play(now, focus)} disabled={!now.url}>
          <Icon name={playing ? "pause" : "play"} size={16} /> {playing ? "Pause" : "Play"}
        </button>
        <button data-action="stage.pick-voice" type="button" className="btn btn-primary" onClick={() => void pick(now)} disabled={!now.voiceId}>
          Pick {name}
        </button>
      </div>
    </div>
  );
}
